import React from "react";
import PropTypes from "prop-types";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";

const FLAG_ICONS_BASE_URL =
  "https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.3.2/flags/4x3";

const useStyles = makeStyles((theme) => ({
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    verticalAlign: "middle",
    cursor: "default",
  },
  flag: {
    width: 18,
    height: 13,
    borderRadius: 2,
    objectFit: "cover",
    flexShrink: 0,
    border: `1px solid ${theme.palette.divider}`,
  },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    lineHeight: 1,
    color: theme.palette.text.primary,
  },
  source: {
    fontSize: "0.62rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    lineHeight: 1,
  },
  stack: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0),
  },
  empty: {
    color: theme.palette.text.disabled,
  },
}));

export function normalizeAdminCountryCode(code) {
  const raw = String(code || "").trim().toUpperCase();
  if (!raw) return "";
  return raw === "UK" ? "GB" : raw;
}

export function resolveAdminFlagImageUrl(code, providedUrl) {
  if (typeof providedUrl === "string" && providedUrl.trim()) {
    return providedUrl.trim();
  }

  const normalized = normalizeAdminCountryCode(code).toLowerCase();
  if (!/^[a-z]{2}$/.test(normalized)) {
    return null;
  }

  return `${FLAG_ICONS_BASE_URL}/${normalized}.svg`;
}

export default function CountryBadge({
  code,
  displayName,
  flagImageUrl,
  sourceLabel,
  tooltipSuffix,
}) {
  const classes = useStyles();
  const normalizedCode = normalizeAdminCountryCode(code);
  if (!normalizedCode) {
    return null;
  }

  const flagUrl = resolveAdminFlagImageUrl(normalizedCode, flagImageUrl);
  const fullName = displayName || normalizedCode;
  const qualifier = sourceLabel || tooltipSuffix;
  const tooltip = qualifier ? `${fullName} (${qualifier})` : fullName;

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <span className={classes.badge} aria-label={tooltip}>
        {flagUrl ? (
          <img className={classes.flag} src={flagUrl} alt="" />
        ) : null}
        <span className={classes.code}>{normalizedCode}</span>
        {sourceLabel ? <span className={classes.source}>{sourceLabel}</span> : null}
      </span>
    </Tooltip>
  );
}

CountryBadge.propTypes = {
  code: PropTypes.string,
  displayName: PropTypes.string,
  flagImageUrl: PropTypes.string,
  sourceLabel: PropTypes.string,
  tooltipSuffix: PropTypes.string,
};

CountryBadge.defaultProps = {
  code: "",
  displayName: "",
  flagImageUrl: "",
  sourceLabel: "",
  tooltipSuffix: "",
};

export function UserCountryCell({ row }) {
  const classes = useStyles();
  const userCode = normalizeAdminCountryCode(row?.country);
  const geoCode = normalizeAdminCountryCode(row?.geoDetectedCountry);

  if (!userCode && !geoCode) {
    return <span className={classes.empty}>—</span>;
  }

  if (userCode && geoCode && userCode === geoCode) {
    return (
      <CountryBadge
        code={userCode}
        displayName={row?.countryDisplayName || row?.geoDetectedCountryDisplayName}
        flagImageUrl={row?.countryFlagImageUrl || row?.geoDetectedCountryFlagImageUrl}
        tooltipSuffix="also detected by GeoIP"
      />
    );
  }

  if (userCode && !geoCode) {
    return (
      <CountryBadge
        code={userCode}
        displayName={row?.countryDisplayName}
        flagImageUrl={row?.countryFlagImageUrl}
      />
    );
  }

  if (!userCode && geoCode) {
    return (
      <CountryBadge
        code={geoCode}
        displayName={row?.geoDetectedCountryDisplayName}
        flagImageUrl={row?.geoDetectedCountryFlagImageUrl}
        sourceLabel="GeoIP"
      />
    );
  }

  return (
    <div className={classes.stack}>
      <CountryBadge
        code={userCode}
        displayName={row?.countryDisplayName}
        flagImageUrl={row?.countryFlagImageUrl}
      />
      <CountryBadge
        code={geoCode}
        displayName={row?.geoDetectedCountryDisplayName}
        flagImageUrl={row?.geoDetectedCountryFlagImageUrl}
        sourceLabel="GeoIP"
      />
    </div>
  );
}

UserCountryCell.propTypes = {
  row: PropTypes.object,
};

UserCountryCell.defaultProps = {
  row: null,
};
